// src/pages/VehiculePage.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Space, 
  Table, 
  Tag, 
  Input, 
  Select, 
  Modal, 
  Form,
  DatePicker,
  Progress,
  Divider,
  Badge,
  Tooltip,
  Avatar,
  Popconfirm,
  Upload,
  message,
  Pagination
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  CarOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  CalendarOutlined,
  GasStationOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  UploadOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const VehiculePage = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    assuranceStatus: ''
  });

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    enMaintenance: 0,
    assuranceExpiree: 0
  });

  // Options pour les filtres
  const statusOptions = ['Actif', 'Inactif', 'En maintenance', 'Hors service'];
  const typeOptions = ['Camion', 'Voiture', 'Pick-up', 'Utilitaire', 'Poids lourd'];
  const assuranceStatusOptions = ['Valide', 'Expirée', 'À renouveler'];

  // Données initiales (mock)
  const initialData = [
    { 
      id: 'V001', 
      immatriculation: 'DK-1234-AB', 
      marque: 'Mercedes-Benz', 
      modele: 'Actros 1845',
      type: 'Poids lourd',
      annee: 2022,
      kilometrage: 125000,
      capacite: '25 tonnes',
      statut: 'Actif',
      assuranceStatus: 'Valide',
      assuranceExpiration: '2024-12-31',
      derniereMaintenance: '2024-01-15',
      prochaineMaintenance: '2024-07-15',
      consommation: 25.5,
      chauffeur: 'Mamadou Diallo',
      note: 'Véhicule principal'
    },
    { 
      id: 'V002', 
      immatriculation: 'DK-5678-CD', 
      marque: 'Toyota', 
      modele: 'Hilux',
      type: 'Pick-up',
      annee: 2023,
      kilometrage: 35000,
      capacite: '1 tonne',
      statut: 'Actif',
      assuranceStatus: 'À renouveler',
      assuranceExpiration: '2024-03-31',
      derniereMaintenance: '2024-01-10',
      prochaineMaintenance: '2024-04-10',
      consommation: 12.8,
      chauffeur: 'Issa Konaté',
      note: 'Véhicule de service'
    },
    { 
      id: 'V003', 
      immatriculation: 'DK-9012-EF', 
      marque: 'Renault', 
      modele: 'Master',
      type: 'Utilitaire',
      annee: 2021,
      kilometrage: 85000,
      capacite: '3 tonnes',
      statut: 'En maintenance',
      assuranceStatus: 'Valide',
      assuranceExpiration: '2024-06-30',
      derniereMaintenance: '2024-01-05',
      prochaineMaintenance: '2024-04-05',
      consommation: 15.2,
      chauffeur: '',
      note: 'En révision générale'
    },
    { 
      id: 'V004', 
      immatriculation: 'DK-3456-GH', 
      marque: 'Volvo', 
      modele: 'FH16',
      type: 'Poids lourd',
      annee: 2020,
      kilometrage: 185000,
      capacite: '40 tonnes',
      statut: 'Actif',
      assuranceStatus: 'Expirée',
      assuranceExpiration: '2024-01-15',
      derniereMaintenance: '2023-12-20',
      prochaineMaintenance: '2024-03-20',
      consommation: 32.1,
      chauffeur: 'Abdoulaye Ndiaye',
      note: 'Assurance à régulariser'
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const fetchData = () => {
    setLoading(true);
    // Simulation API call
    setTimeout(() => {
      setData(initialData);
      setFilteredData(initialData);
      calculateStats(initialData);
      setLoading(false);
    }, 500);
  };

  const calculateStats = (dataArray) => {
    const total = dataArray.length;
    const actifs = dataArray.filter(item => item.statut === 'Actif').length;
    const enMaintenance = dataArray.filter(item => item.statut === 'En maintenance').length;
    const assuranceExpiree = dataArray.filter(item => item.assuranceStatus === 'Expirée' || item.assuranceStatus === 'À renouveler').length;
    
    setStats({ total, actifs, enMaintenance, assuranceExpiree });
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.immatriculation.toLowerCase().includes(searchLower) ||
        item.marque.toLowerCase().includes(searchLower) ||
        item.modele.toLowerCase().includes(searchLower) ||
        item.chauffeur.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(item => item.statut === filters.status);
    }

    if (filters.type) {
      filtered = filtered.filter(item => item.type === filters.type);
    }

    if (filters.assuranceStatus) {
      filtered = filtered.filter(item => item.assuranceStatus === filters.assuranceStatus);
    }

    setFilteredData(filtered);
    calculateStats(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const showModal = (vehicle = null) => {
    setEditingVehicle(vehicle);
    if (vehicle) {
      form.setFieldsValue({
        ...vehicle,
        annee: dayjs(vehicle.annee.toString())
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingVehicle(null);
    form.resetFields();
  };

  const handleSubmit = (values) => {
    setLoading(true);
    
    setTimeout(() => {
      if (editingVehicle) {
        // Mise à jour
        const updatedData = data.map(item => 
          item.id === editingVehicle.id ? { ...item, ...values, annee: values.annee.year() } : item
        );
        setData(updatedData);
        message.success('Véhicule modifié avec succès!');
      } else {
        // Création
        const newId = `V${(data.length + 101).toString().padStart(3, '0')}`;
        const newRecord = {
          id: newId,
          ...values,
          annee: values.annee.year(),
          kilometrage: 0,
          statut: 'Actif',
          assuranceStatus: 'Valide',
          dateCreation: new Date().toISOString()
        };
        setData([...data, newRecord]);
        message.success('Véhicule ajouté avec succès!');
      }
      
      setLoading(false);
      setIsModalVisible(false);
      form.resetFields();
    }, 500);
  };

  const handleDelete = (record) => {
    setLoading(true);
    
    setTimeout(() => {
      const filtered = data.filter(item => item.id !== record.id);
      setData(filtered);
      message.success('Véhicule supprimé avec succès!');
      setLoading(false);
    }, 300);
  };

  const renderStatutBadge = (statut) => {
    switch (statut) {
      case 'Actif':
        return <Tag color="green" className="font-medium">Actif</Tag>;
      case 'En maintenance':
        return <Tag color="orange" className="font-medium">En maintenance</Tag>;
      case 'Inactif':
        return <Tag color="red" className="font-medium">Inactif</Tag>;
      case 'Hors service':
        return <Tag color="gray" className="font-medium">Hors service</Tag>;
      default:
        return <Tag>{statut}</Tag>;
    }
  };

  const renderAssuranceBadge = (status) => {
    switch (status) {
      case 'Valide':
        return <Tag color="green" className="font-medium">Valide</Tag>;
      case 'Expirée':
        return <Tag color="red" className="font-medium">Expirée</Tag>;
      case 'À renouveler':
        return <Tag color="orange" className="font-medium">À renouveler</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getMaintenanceProgress = (vehicle) => {
    const lastMaintenance = dayjs(vehicle.derniereMaintenance);
    const nextMaintenance = dayjs(vehicle.prochaineMaintenance);
    const now = dayjs();
    const totalDays = nextMaintenance.diff(lastMaintenance, 'day');
    const daysPassed = now.diff(lastMaintenance, 'day');
    
    return Math.min(Math.round((daysPassed / totalDays) * 100), 100);
  };

  const columns = [
    {
      title: 'Immatriculation',
      dataIndex: 'immatriculation',
      key: 'immatriculation',
      width: 120,
      render: (text) => (
        <Tag color="blue" className="font-mono font-bold text-sm">
          {text}
        </Tag>
      ),
    },
    {
      title: 'Véhicule',
      key: 'vehicule',
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar 
            size="large"
            className="bg-blue-100 text-blue-600 mr-3"
            icon={<CarOutlined />}
          />
          <div>
            <Text strong className="block">{record.marque} {record.modele}</Text>
            <Text type="secondary" className="text-xs">
              {record.type} • {record.annee} • {record.capacite}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Kilométrage',
      dataIndex: 'kilometrage',
      key: 'kilometrage',
      align: 'right',
      render: (km) => (
        <div className="text-right">
          <Text strong>{km.toLocaleString('fr-FR')}</Text>
          <Text type="secondary" className="text-xs block">km</Text>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      key: 'statut',
      width: 120,
      render: renderStatutBadge,
    },
    {
      title: 'Assurance',
      dataIndex: 'assuranceStatus',
      key: 'assuranceStatus',
      width: 100,
      render: renderAssuranceBadge,
    },
    {
      title: 'Maintenance',
      key: 'maintenance',
      width: 150,
      render: (_, record) => {
        const progress = getMaintenanceProgress(record);
        let status = 'success';
        if (progress > 80) status = 'exception';
        else if (progress > 60) status = 'active';
        
        return (
          <div>
            <Progress 
              percent={progress} 
              size="small" 
              status={status}
              showInfo={false}
            />
            <Text type="secondary" className="text-xs">
              Prochaine: {dayjs(record.prochaineMaintenance).format('DD/MM')}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              onClick={() => showModal(record)}
              className="text-blue-500"
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => showModal(record)}
              className="text-green-500"
            />
          </Tooltip>
          <Popconfirm 
            title="Supprimer ce véhicule ?"
            description="Êtes-vous sûr de vouloir supprimer ce véhicule ?"
            onConfirm={() => handleDelete(record)}
            okText="Oui"
            cancelText="Non"
            okType="danger"
          >
            <Tooltip title="Supprimer">
              <Button 
                type="link" 
                icon={<DeleteOutlined />} 
                className="text-red-500"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div>
      {/* En-tête */}
      <div className="mb-6">
        <Title level={2} className="m-0">Gestion des Véhicules</Title>
        <Text className="text-gray-600 mt-2 block">
          Gérez votre flotte de véhicules, assurances et entretiens
        </Text>
      </div>

      {/* Statistiques simplifiées avec texte centré */}
      <div className="mb-6">
        <Row gutter={[8, 8]}>
          <Col xs={6} sm={6} lg={6}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <CarOutlined className="text-blue-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Total véhicules</Text>
              <Text className="text-2xl font-bold block">{stats.total}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={6}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <CheckCircleOutlined className="text-green-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Actifs</Text>
              <Text className="text-2xl font-bold text-green-600 block">{stats.actifs}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={6}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <ToolOutlined className="text-orange-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">En maintenance</Text>
              <Text className="text-2xl font-bold text-orange-600 block">{stats.enMaintenance}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={6}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <WarningOutlined className="text-red-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Assurance</Text>
              <Text className="text-2xl font-bold text-red-600 block">{stats.assuranceExpiree}</Text>
            </div>
          </Col>
        </Row>
      </div>

      {/* Barre d'actions et filtres */}
      <Card className="modern-card mb-6">
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="btn-primary"
                onClick={() => showModal()}
              >
                Nouveau Véhicule
              </Button>
              <Upload>
                <Button icon={<UploadOutlined />}>
                  Importer véhicules
                </Button>
              </Upload>
              <Button icon={<DownloadOutlined />}>
                Exporter liste
              </Button>
              <Button icon={<HistoryOutlined />}>
                Historique entretiens
              </Button>
            </Space>
          </Col>
          <Col>
            <Space size="middle" wrap>
              <Input
                placeholder="Rechercher immatriculation, marque..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
                className="w-48"
              />
              <Select
                placeholder="Type de véhicule"
                value={filters.type}
                onChange={(value) => handleFilterChange('type', value)}
                allowClear
                className="w-40"
              >
                {typeOptions.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
              <Select
                placeholder="Statut"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                className="w-40"
              >
                {statusOptions.map(statut => (
                  <Option key={statut} value={statut}>{statut}</Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tableau des véhicules - Version compacte */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden modern-card">
        {/* En-tête compact */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center">
            <CarOutlined className="text-gray-400 mr-2" />
            <Text className="text-gray-700 font-medium">Flotte de véhicules</Text>
          </div>
          <Text className="text-sm text-gray-500">
            {filteredData.length} véhicules • {stats.actifs} disponibles
          </Text>
        </div>

        {/* Tableau compact */}
        <div className="overflow-x-auto">
          <Table
            columns={columns.map(col => ({
              ...col,
              className: 'py-2'
            }))}
            dataSource={paginatedData}
            rowKey="id"
            pagination={false}
            loading={loading}
            size="small"
            className="[&_.ant-table-thead>tr>th]:bg-green-700 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-thead>tr>th]:text-white [&_.ant-table-tbody>tr>td]:py-3"
            rowClassName="border-b border-gray-100 hover:bg-green-50"
          />
        </div>

        {/* Pied de tableau avec totaux */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Total véhicules :</span> {filteredData.length}
              </Text>
              <Divider type="vertical" />
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Disponibles :</span> 
                <span className="ml-1 font-bold text-green-600">{stats.actifs}</span>
              </Text>
              <Divider type="vertical" />
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">En maintenance :</span> 
                <span className="ml-1 font-bold text-orange-600">{stats.enMaintenance}</span>
              </Text>
              <Divider type="vertical" />
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Kilométrage moyen :</span> 
                <span className="ml-1 font-bold text-blue-600">
                  {Math.round(data.reduce((acc, v) => acc + v.kilometrage, 0) / data.length).toLocaleString('fr-FR')} km
                </span>
              </Text>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                size="small"
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              <Text className="text-sm">
                Page {currentPage} sur {Math.ceil(filteredData.length / pageSize)}
              </Text>
              <Button
                size="small"
                icon={<RightOutlined />}
                disabled={currentPage === Math.ceil(filteredData.length / pageSize)}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </div>
          </div>
        </div>

        {/* Pagination détaillée */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <Text className="text-gray-500 text-sm">
                Affichage <span className="font-semibold text-gray-700">
                  {Math.min((currentPage - 1) * pageSize + 1, filteredData.length)}-{Math.min(currentPage * pageSize, filteredData.length)}
                </span> sur <span className="font-semibold text-primary-500">{filteredData.length}</span> véhicules
              </Text>
            </div>
            
            <div className="flex items-center space-x-4">
              <Select
                value={pageSize}
                onChange={(value) => setPageSize(value)}
                size="small"
                className="w-28"
              >
                <Option value={5}>5 éléments/page</Option>
                <Option value={10}>10 éléments/page</Option>
                <Option value={20}>20 éléments/page</Option>
                <Option value={50}>50 éléments/page</Option>
              </Select>
              
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showSizeChanger={false}
                showQuickJumper
                size="small"
                className="[&_.ant-pagination-item]:rounded-full [&_.ant-pagination-item-active]:bg-green-600 [&_.ant-pagination-item-active]:border-green-600 [&_.ant-pagination-item-active>a]:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour ajouter/modifier un véhicule */}
      <Modal
        title={editingVehicle ? "Modifier le Véhicule" : "Nouveau Véhicule"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="modern-form"
          onFinish={handleSubmit}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="immatriculation"
                label="Immatriculation"
                rules={[{ required: true, message: 'Veuillez saisir l\'immatriculation' }]}
              >
                <Input 
                  placeholder="Ex: DK-1234-AB" 
                  prefix={<CarOutlined />}
                  className="uppercase"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type de véhicule"
                rules={[{ required: true, message: 'Veuillez sélectionner le type' }]}
              >
                <Select placeholder="Sélectionnez le type">
                  {typeOptions.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="marque"
                label="Marque"
                rules={[{ required: true, message: 'Veuillez saisir la marque' }]}
              >
                <Input placeholder="Ex: Mercedes-Benz" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="modele"
                label="Modèle"
                rules={[{ required: true, message: 'Veuillez saisir le modèle' }]}
              >
                <Input placeholder="Ex: Actros 1845" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="annee"
                label="Année"
                rules={[{ required: true, message: 'Veuillez sélectionner l\'année' }]}
              >
                <DatePicker 
                  picker="year" 
                  className="w-full"
                  placeholder="Sélectionnez l'année"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="capacite"
                label="Capacité"
                rules={[{ required: true, message: 'Veuillez saisir la capacité' }]}
              >
                <Input placeholder="Ex: 25 tonnes" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="kilometrage"
                label="Kilométrage"
                rules={[{ required: true, message: 'Veuillez saisir le kilométrage' }]}
              >
                <Input 
                  type="number"
                  placeholder="Ex: 125000"
                  addonAfter="km"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="statut"
                label="Statut"
                rules={[{ required: true, message: 'Veuillez sélectionner le statut' }]}
                initialValue="Actif"
              >
                <Select placeholder="Sélectionnez le statut">
                  {statusOptions.map(statut => (
                    <Option key={statut} value={statut}>{statut}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="chauffeur"
                label="Chauffeur assigné"
              >
                <Input placeholder="Ex: Mamadou Diallo" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="assuranceExpiration"
                label="Date expiration assurance"
              >
                <DatePicker 
                  className="w-full"
                  placeholder="Sélectionnez la date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="prochaineMaintenance"
                label="Prochaine maintenance"
              >
                <DatePicker 
                  className="w-full"
                  placeholder="Sélectionnez la date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="note"
                label="Notes"
              >
                <Input.TextArea 
                  placeholder="Informations supplémentaires, historique, observations..." 
                  rows={3}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="text-right mt-6">
            <Space>
              <Button onClick={handleCancel}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} className="btn-primary">
                {editingVehicle ? 'Modifier' : 'Ajouter'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default VehiculePage;